import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Type definition for common date format patterns / 一般的な日付フォーマットパターンの型定義
 *
 * @property {string} yearMonth - Format for year and month / 年月のフォーマット
 * @property {string} yearMonthDay - Format for year, month, and day / 年月日のフォーマット
 * @property {string} yearMonthDayTime - Format for year, month, day, and time / 年月日時のフォーマット
 * @property {string} monthDay - Format for month and day / 月日のフォーマット
 */
export type DateFormats = {
  yearMonth: string;
  yearMonthDay: string;
  yearMonthDayTime: string;
  monthDay: string;
};

/**
 * Formats a date input into a specified format string / 日付入力を指定されたフォーマット文字列に変換します
 *
 * This function uses dayjs to parse and format dates. It accepts various input types and can parse
 * dates with a specific input format if provided.
 * dayjsを使用して日付を解析およびフォーマットします。様々な入力タイプを受け付け、
 * 指定された入力フォーマットがある場合はそれを使用して日付を解析できます。
 *
 * @param {string | number | Date} input - The date input to format / フォーマットする日付入力
 * @param {string} format - The desired output format (e.g., 'YYYY-MM-DD') / 出力フォーマット（例：'YYYY-MM-DD'）
 * @param {string} [inputFormat] - Optional input format for parsing string dates / 文字列日付を解析するためのオプションの入力フォーマット
 * @returns {string} The formatted date string, or empty string if invalid / フォーマットされた日付文字列、無効な場合は空文字列
 *
 * @example
 * // Returns "2023-12-25" / "2023-12-25"を返します
 * formatDate('2023/12/25', 'YYYY-MM-DD', 'YYYY/MM/DD');
 *
 * @example
 * // Returns "2023年12月" / "2023年12月"を返します
 * formatDate(new Date('2023-12-25'), 'YYYY年MM月');
 *
 * @example
 * // Returns "" for invalid date / 無効な日付の場合は""を返します
 * formatDate('invalid', 'YYYY-MM-DD');
 */
export const formatDate = (input: string | number | Date, format: string, inputFormat?: string): string => {
  const inputStr = typeof input === 'number' ? String(input) : input;

  const date = inputFormat ? dayjs(inputStr, inputFormat) : dayjs(inputStr);

  if (!date.isValid()) return '';

  return date.format(format);
};
